import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { dotenv } from '@utils/constants/env';
import amplifyConfig from '../../amplifyconfiguration.json';

interface CognitoConfig {
  region: string;
  identityPoolId: string;
  userPoolId: string;
}

/**
 * Extracts Cognito configuration from amplifyconfiguration.json
 * @returns {CognitoConfig} - Cognito configuration
 */
export const getCognitoConfigFromAmplify = (): CognitoConfig => {
  const region = amplifyConfig.aws_cognito_region || '';
  const userPoolId = amplifyConfig.aws_user_pools_id || '';
  const identityPoolId = amplifyConfig.aws_cognito_identity_pool_id || '';

  if (!region || !userPoolId || !identityPoolId) {
    throw new Error(
      `Cognito configuration not found. Found: region=${region}, userPoolId=${userPoolId}, identityPoolId=${identityPoolId}`
    );
  }

  return {
    region,
    identityPoolId,
    userPoolId
  };
};

/**
 * Alternative: Get configuration from environment variables or external source
 * @param {any} configData - Configuration data (optional, can be from API)
 * @returns {CognitoConfig} - Cognito configuration
 */
export const getCognitoConfig = (configData?: any): CognitoConfig => {
  // If no config data provided, get from Amplify config file
  if (!configData) {
    return getCognitoConfigFromAmplify();
  }

  // Otherwise extract from provided data
  const data = configData?.data || configData;

  return {
    region: data?.cognitoRegion || data?.COGNITO_REGION || '',
    identityPoolId: data?.identityPoolId || data?.IDENTITY_POOL_ID || '',
    userPoolId: data?.userPoolId || data?.COGNITO_USER_POOL_ID || ''
  };
};

/**
 * Uploads a file to S3 using Cognito authentication
 * @param {File} file - File object to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Optional folder path in S3 (default: 'profile')
 * @param {string} customPath - Optional custom path override
 * @returns {Promise<string>} - Uploaded file URL
 */
export const uploadToS3 = async (
  file: File,
  idToken: string,
  userId?: string,
  folder: string = 'profile',
  customPath?: string
): Promise<string> => {
  if (!file) throw new Error('No file provided');
  if (!idToken) throw new Error('Missing Cognito ID token');
  if (!userId) throw new Error('Missing user ID');

  const { region, identityPoolId, userPoolId } = getCognitoConfig();

  // 🔐 Create S3 client with temporary credentials
  const s3 = new S3Client({
    region,
    credentials: fromCognitoIdentityPool({
      identityPoolId,
      logins: {
        [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken
      },
      clientConfig: { region }
    })
  });

  // 🧠 Unique file path
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const key = customPath
    ? `${customPath}${timestamp}_${sanitizedFileName}`
    : `${dotenv.ENV}/users/${userId}/${folder}/${timestamp}_${sanitizedFileName}`;

  // Convert File to ArrayBuffer for browser compatibility
  const fileBuffer = await file.arrayBuffer();

  // 🧾 Prepare the upload command
  const command = new PutObjectCommand({
    Bucket: dotenv.BUCKET,
    Key: key,
    Body: new Uint8Array(fileBuffer),
    ContentType: file.type
  });

  try {
    // 🚀 Upload to S3
    await s3.send(command);

    // Return the full URL
    return `https://${dotenv.BUCKET}.s3.${region}.amazonaws.com/${key}`;
  } catch (error: any) {
    console.error('S3 Upload Error:', error);
    throw new Error(`Failed to upload file: ${error.message || error}`);
  }
};

/**
 * Uploads multiple photos to S3 with progress tracking
 * @param {File[]} files - Array of File objects to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Folder name (e.g., 'photos', 'videos', 'recipes')
 * @param {Function} onProgress - Callback function (currentIndex, total, fileName)
 * @returns {Promise<string[]>} - Array of uploaded file URLs
 */
export const uploadMultiplePhotos = async (
  files: File[],
  idToken: string,
  userId: string,
  folder: string,
  onProgress?: (current: number, total: number, fileName: string) => void,
  subscriberId?: string
): Promise<string[]> => {
  if (!files || files.length === 0) throw new Error('No files provided');
  if (!idToken) throw new Error('Missing Cognito ID token');
  if (!userId) throw new Error('Missing user ID');

  const uploadedUrls: string[] = [];
  const customPath = `${dotenv.ENV}/temp/${subscriberId}/${folder}/`;

  try {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Upload file
      const url = await uploadToS3(file, idToken, userId, folder, customPath);
      uploadedUrls.push(url);

      // Trigger progress callback
      if (onProgress) {
        onProgress(i + 1, files.length, file.name);
      }
    }

    return uploadedUrls;
  } catch (error: any) {
    console.error('Multiple Photos Upload Error:', error);
    throw new Error(`Failed to upload photos: ${error.message || error}`);
  }
};

/**
 * Uploads multiple files to S3 using Cognito authentication
 * @param {File[]} files - Array of File objects to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Optional folder path in S3 (default: 'profile')
 * @returns {Promise<string[]>} - Array of uploaded file URLs
 */
export const uploadMultipleToS3 = async (
  files: File[],
  idToken: string,
  userId: string,
  folder: string = 'attachments'
): Promise<string[]> => {
  if (!files || files.length === 0) throw new Error('No files provided');
  if (!idToken) throw new Error('Missing Cognito ID token');
  if (!userId) throw new Error('Missing user ID');

  try {
    // Upload all files in parallel
    const uploadPromises = files.map(file =>
      uploadToS3(file, idToken, userId, folder)
    );

    const uploadedUrls = await Promise.all(uploadPromises);
    return uploadedUrls;
  } catch (error: any) {
    console.error('Multiple S3 Upload Error:', error);
    throw new Error(`Failed to upload files: ${error.message || error}`);
  }
};

/**
 * Validates if file is an image or video and within size limit
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum file size in MB (default: 50MB for videos)
 * @returns {boolean} - True if valid
 */
export const validateImageFile = (
  file: File,
  maxSizeMB: number = 50
): { valid: boolean; error?: string } => {
  const validTypes = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/webp',
    'image/gif',
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
    'video/webm'
  ];
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error:
        'Invalid file type. Only images (JPEG, PNG, WEBP, GIF) and videos (MP4, MOV, AVI, MKV, WEBM) are allowed.'
    };
  }

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit.`
    };
  }

  return { valid: true };
};

/**
 * Validates multiple files
 * @param {File[]} files - Files to validate
 * @param {number} maxSizeMB - Maximum file size in MB per file
 * @param {number} maxFiles - Maximum number of files allowed
 * @returns {object} - Validation result
 */
export const validateMultipleFiles = (
  files: File[],
  maxSizeMB: number = 50,
  maxFiles: number = 5
): { valid: boolean; error?: string } => {
  if (!files || files.length === 0) {
    return { valid: false, error: 'No files selected' };
  }

  if (maxFiles > 1 && files.length > maxFiles) {
    return { valid: false, error: `Maximum ${maxFiles} files allowed` };
  }

  for (const file of files) {
    const validation = validateImageFile(file, maxSizeMB);
    if (!validation.valid) {
      return validation;
    }
  }

  return { valid: true };
};
